import './MissionModal.scss';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, CircularProgress } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { COPY } from '../../assets/copy/copy';
import { FALLBACK_MISSION_IMAGE_URL } from '../../assets/FALLBACK_MISSION_IMAGE_URL.js';
import { BackArrow, CloseIcon } from '../../assets/svgs';
import { TransitionModal } from '../../components/TransitionModal/TransitionModal';
import { VideoDisplay } from '../../components/VideoDisplay/videoDisplay';
import type { GetMissionModalDataQuery } from '../../models/__generated__/graphql';
import {
  ACTION_PERFORMED,
  SET_MISSION_LIKE,
} from '../../store/mutations/mutations';
import { GET_MISSION_MODAL_DATA } from '../../store/queries/containerQueries';
import { ISODateToDate } from '../../utils/formatters';
import { logError } from '../../utils/logError.js';
import { BunchBallQuiz } from './BunchBallQuiz/BunchBallQuiz';
import { MissionDetails } from './MissionDetails/MissionDetails';

type MissionModalProps = {
  missionId: string | null | undefined;
  handleClose: () => void;
};

enum ModalRoute {
  DETAILS,
  VIDEO,
  QUIZ,
}

export function MissionModal(props: MissionModalProps): JSX.Element {
  const [modalRoute, setModalRoute] = useState<ModalRoute>(ModalRoute.DETAILS);
  const [newestData, setNewestData] = useState<GetMissionModalDataQuery | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  // loading = true;
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number | null>(
    null
  );

  const isOpen = useMemo(() => !!props.missionId, [props.missionId]);

  const [fetchData, { data, refetch }] = useLazyQuery(GET_MISSION_MODAL_DATA);

  const [setMissionLike] = useMutation(SET_MISSION_LIKE);

  useEffect(() => {
    if (props.missionId && isOpen) {
      fetchData({ variables: { missionId: props.missionId } }).catch(logError);
    }
  }, [fetchData, isOpen, loading, props.missionId]);

  useEffect(() => {
    if (isOpen && props.missionId) {
      if (data?.me?.mission?.id === props.missionId) {
        setLoading(false);
        setNewestData(data);
        setModalRoute(ModalRoute.DETAILS);
      } else {
        setSelectedTaskIndex(null);
        setNewestData(null);
        setLoading(true);
      }
    }
  }, [data, isOpen, props.missionId]);

  const [actionPerf, { data: mutData }] = useMutation(ACTION_PERFORMED);

  useEffect(() => {
    // dont refetch data after a video has begun being watched or the user is in the middle of the quiz
    if (
      !mutData ||
      modalRoute === ModalRoute.VIDEO ||
      modalRoute === ModalRoute.QUIZ
    )
      return;
    const timeout = setTimeout(() => {
      refetch();
    }, 2000);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [modalRoute, mutData, refetch]);

  const mission = newestData?.me?.mission;
  const tasks = mission?.tasks;

  const handleChangeMissionLike = useCallback(
    async (iLike: boolean) => {
      if (!mission) return;

      await setMissionLike({
        variables: {
          iLike,
          missionId: mission.id,
        },
      });
    },
    [setMissionLike, mission]
  );

  const displayVideo = useCallback((taskNumber: number) => {
    setSelectedTaskIndex(taskNumber);
    setModalRoute(ModalRoute.VIDEO);
  }, []);

  const displayQuiz = useCallback((taskNumber: number) => {
    setSelectedTaskIndex(taskNumber);
    setModalRoute(ModalRoute.QUIZ);
  }, []);

  const displayDetails = useCallback(() => {
    setSelectedTaskIndex(null);
    setModalRoute(ModalRoute.DETAILS);
  }, []);

  const videoDisplay = useMemo(() => {
    if (!tasks || selectedTaskIndex === null) {
      return <div />;
    }

    const task = tasks[selectedTaskIndex];

    if (task.__typename !== 'Video') {
      return <div />;
    }

    return (
      <div className="modal-content">
        <h5> {task.displayText} </h5>
        <VideoDisplay
          actionPerf={actionPerf}
          videoUrl={task.url ?? ''}
          actionName={task.actionName ?? ''}
        />
      </div>
    );
  }, [selectedTaskIndex, tasks, actionPerf]);

  const quizDisplay = useMemo(() => {
    if (!tasks || selectedTaskIndex === null) {
      return <div />;
    }

    const task = tasks[selectedTaskIndex];

    if (task.__typename !== 'Quiz') {
      return <div />;
    }

    return <BunchBallQuiz {...task} />;
  }, [selectedTaskIndex, tasks]);

  const missionDetailsDisplay = useMemo(() => {
    if (!mission) {
      return <div />;
    }

    return (
      <MissionDetails
        actionPerf={actionPerf}
        description={mission.description}
        displayQuiz={displayQuiz}
        displayVideo={displayVideo}
        expiringDate={ISODateToDate(mission.expiringDate)}
        expiringSoon={mission.expiringSoon}
        onChangeMissionLike={handleChangeMissionLike}
        id={mission.id}
        iLike={mission.iLike}
        inProgress={mission.inProgress}
        locked={mission.lockedStatus?.reasons}
        name={mission.name}
        refetch={refetch}
        repeats={mission.repeats}
        rewards={mission.rewards}
        tasks={tasks}
        totalLikes={mission.totalLikes}
      />
    );
  }, [
    actionPerf,
    displayQuiz,
    displayVideo,
    handleChangeMissionLike,
    mission,
    refetch,
    tasks,
  ]);

  const switchContent = useMemo(() => {
    switch (modalRoute) {
      case ModalRoute.DETAILS:
        return missionDetailsDisplay;
      case ModalRoute.VIDEO:
        return videoDisplay;
      case ModalRoute.QUIZ:
        return quizDisplay;
    }
  }, [modalRoute, missionDetailsDisplay, videoDisplay, quizDisplay]);

  const handleClickBack = useCallback(() => {
    refetch().catch(logError);
    displayDetails();
  }, [refetch, displayDetails]);

  return (
    <TransitionModal
      onClose={props.handleClose}
      open={isOpen}
      paperClassName="mission-modal"
    >
      <Button
        // The metrics, position, and theming of this button are overridden in CSS.
        // The only aspect of Material we're using is the hover/click/focus effects.
        // Also, we can't use <IconButton> because it lacks variant="contained".
        aria-label="Close modal"
        className="mission-modal-close-button"
        color="primary"
        onClick={props.handleClose}
        variant="contained"
      >
        <CloseIcon className="close-icon" />
      </Button>
      <div className="mission-modal-scroll">
        <div className="mission-modal-card">
          {modalRoute === ModalRoute.DETAILS ? (
            mission && (
              <img
                alt={`Badge representing ${mission.name} Mission`}
                className={classNames('mission-cover-image', {
                  gray: mission.lockedStatus?.locked === true,
                })}
                src={mission.iconImageUrl ?? FALLBACK_MISSION_IMAGE_URL}
              />
            )
          ) : (
            <div className="mission-back-button-container">
              <Button
                className="header-action-text"
                onClick={handleClickBack}
                startIcon={<BackArrow />}
                variant="text"
              >
                <span>{COPY.MODAL.NON_DETAILS_HEADER}</span>
              </Button>
            </div>
          )}
          {loading ? (
            <div className="center">
              <CircularProgress />
            </div>
          ) : mission === null ? (
            <div className="center">
              <h4>An error has occurred try again.</h4>
            </div>
          ) : (
            switchContent
          )}
        </div>
      </div>
    </TransitionModal>
  );
}
